import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem("language") || "en";
  });

  // Update localStorage whenever language changes
  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  // Sync with Supabase user profile on load and auth change
  useEffect(() => {
    const syncLanguage = (session) => {
      if (session?.user?.user_metadata?.language) {
        const userLang = session.user.user_metadata.language;
        setLanguageState((prev) => {
          if (prev !== userLang) {
            return userLang;
          }
          return prev;
        });
      }
    };

    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      syncLanguage(session);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      syncLanguage(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Update state locally without persisting (for non-auth flows like HomePage)
  const setLanguageLocal = (newLang) => {
    setLanguageState(newLang);
  };

  // Wrapper to update state AND Supabase metadata
  const setLanguage = async (newLang) => {
    setLanguageState(newLang);
    
    // If user is logged in, save preference to Supabase
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await supabase.auth.updateUser({
        data: { language: newLang }
      });

      // Also update USER_PROFILES
      await supabase
        .from('USER_PROFILES')
        .upsert({
          user_id: session.user.id,
          language: newLang
        }, { onConflict: 'user_id' });
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, setLanguageLocal }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}